import { createSlice } from "@reduxjs/toolkit";
import {
  getElecRateConfigsThunk,
  getElecRateConfigByIdThunk,
  createElecRateConfigThunk,
  updateElecRateConfigThunk,
  getElecRecordsThunk,
  getElecRecordByIdThunk,
  createElecRecordThunk,
  createElecRecordsBatchThunk,
  updateElecRecordThunk,
  deleteElecRecordThunk,
  updateElecRecordStatusThunk,
  getElecRecordSummaryTextThunk,
  calculateElecPreviewThunk,
  getElecUnitContextThunk,
  getElecPendingScheduleThunk,
  getWaterRateConfigsThunk,
  getWaterRateConfigByIdThunk,
  createWaterRateConfigThunk,
  updateWaterRateConfigThunk,
  getWaterPendingScheduleThunk,
  getWaterUnitContextThunk,
  getWaterRecordsThunk,
  getWaterRecordByIdThunk,
  createWaterRecordThunk,
  createWaterRecordsBatchThunk,
  updateWaterRecordThunk,
  deleteWaterRecordThunk,
  updateWaterRecordStatusThunk,
  calculateWaterPreviewThunk,
} from "state/action/utility_record";

const asyncState = (overrides = {}) => ({
  data: null,
  isPending: false,
  isFulfilled: false,
  isRejected: false,
  err: null,
  ...overrides,
});

function addAsyncCases(builder, thunk, stateKey) {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey] = asyncState({ isPending: true });
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey] = asyncState({ isFulfilled: true, data: action.payload });
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey] = asyncState({ isRejected: true, err: action.error.message });
    });
}

const initialState = {
  // ── Electricity Rate Configs ───────────────────────────────────────────────
  elecRateConfigs: asyncState(),
  elecRateConfigDetail: asyncState(),
  createElecRateConfig: asyncState(),
  updateElecRateConfig: asyncState(),

  // ── Electricity Records ────────────────────────────────────────────────────
  elecRecords: asyncState(),
  elecRecordDetail: asyncState(),
  createElecRecord: asyncState(),
  createElecRecordsBatch: asyncState(),
  updateElecRecord: asyncState(),
  deleteElecRecord: asyncState(),
  updateElecRecordStatus: asyncState(),
  elecSummaryText: asyncState(),
  elecPreview: asyncState(),
  elecUnitContext: asyncState(),
  elecPendingSchedule: asyncState(),

  // ── Water Rate Configs ─────────────────────────────────────────────────────
  waterRateConfigs: asyncState(),
  waterRateConfigDetail: asyncState(),
  createWaterRateConfig: asyncState(),
  updateWaterRateConfig: asyncState(),

  // ── Water Records ──────────────────────────────────────────────────────────
  waterPendingSchedule: asyncState(),
  waterUnitContext: asyncState(),
  waterRecords: asyncState(),
  waterRecordDetail: asyncState(),
  createWaterRecord: asyncState(),
  createWaterRecordsBatch: asyncState(),
  updateWaterRecord: asyncState(),
  deleteWaterRecord: asyncState(),
  updateWaterRecordStatus: asyncState(),
  waterPreview: asyncState(),

  // ── UI ─────────────────────────────────────────────────────────────────────
  activeTab: "elec",
  elecFilter: { buildingId: "", status: "", month: "", year: new Date().getFullYear(), page: 1 },
  waterFilter: { contractId: "", status: "", month: "", year: new Date().getFullYear(), page: 1 },
};

const utilityRecordSlice = createSlice({
  name: "utilityRecord",
  initialState,
  reducers: {
    setActiveTab: (state, action) => { state.activeTab = action.payload; },
    setElecFilter: (state, action) => { state.elecFilter = { ...state.elecFilter, ...action.payload }; },
    resetElecFilter: (state) => { state.elecFilter = { buildingId: "", status: "", month: "", year: new Date().getFullYear(), page: 1 }; },
    setWaterFilter: (state, action) => { state.waterFilter = { ...state.waterFilter, ...action.payload }; },
    resetWaterFilter: (state) => { state.waterFilter = { contractId: "", status: "", month: "", year: new Date().getFullYear(), page: 1 }; },
    clearCreateElecRecord: (state) => { state.createElecRecord = asyncState(); },
    clearCreateElecRecordsBatch: (state) => { state.createElecRecordsBatch = asyncState(); },
    clearUpdateElecRecord: (state) => { state.updateElecRecord = asyncState(); },
    clearDeleteElecRecord: (state) => { state.deleteElecRecord = asyncState(); },
    clearUpdateElecRecordStatus: (state) => { state.updateElecRecordStatus = asyncState(); },
    clearElecRecordDetail: (state) => { state.elecRecordDetail = asyncState(); },
    clearElecPreview: (state) => { state.elecPreview = asyncState(); },
    clearCreateElecRateConfig: (state) => { state.createElecRateConfig = asyncState(); },
    clearUpdateElecRateConfig: (state) => { state.updateElecRateConfig = asyncState(); },
    clearElecUnitContext: (state) => { state.elecUnitContext = asyncState(); },
    clearCreateWaterRecord: (state) => { state.createWaterRecord = asyncState(); },
    clearCreateWaterRecordsBatch: (state) => { state.createWaterRecordsBatch = asyncState(); },
    clearUpdateWaterRecord: (state) => { state.updateWaterRecord = asyncState(); },
    clearDeleteWaterRecord: (state) => { state.deleteWaterRecord = asyncState(); },
    clearUpdateWaterRecordStatus: (state) => { state.updateWaterRecordStatus = asyncState(); },
    clearWaterRecordDetail: (state) => { state.waterRecordDetail = asyncState(); },
    clearWaterUnitContext: (state) => { state.waterUnitContext = asyncState(); },
    clearWaterPreview: (state) => { state.waterPreview = asyncState(); },
    clearCreateWaterRateConfig: (state) => { state.createWaterRateConfig = asyncState(); },
    clearUpdateWaterRateConfig: (state) => { state.updateWaterRateConfig = asyncState(); },
  },
  extraReducers(builder) {
    // Electricity Rate Configs
    addAsyncCases(builder, getElecRateConfigsThunk, "elecRateConfigs");
    addAsyncCases(builder, getElecRateConfigByIdThunk, "elecRateConfigDetail");
    addAsyncCases(builder, createElecRateConfigThunk, "createElecRateConfig");
    addAsyncCases(builder, updateElecRateConfigThunk, "updateElecRateConfig");
    // Electricity Records
    addAsyncCases(builder, getElecRecordsThunk, "elecRecords");
    addAsyncCases(builder, getElecRecordByIdThunk, "elecRecordDetail");
    addAsyncCases(builder, createElecRecordThunk, "createElecRecord");
    addAsyncCases(builder, createElecRecordsBatchThunk, "createElecRecordsBatch");
    addAsyncCases(builder, updateElecRecordThunk, "updateElecRecord");
    addAsyncCases(builder, deleteElecRecordThunk, "deleteElecRecord");
    addAsyncCases(builder, updateElecRecordStatusThunk, "updateElecRecordStatus");
    addAsyncCases(builder, getElecRecordSummaryTextThunk, "elecSummaryText");
    addAsyncCases(builder, calculateElecPreviewThunk, "elecPreview");
    addAsyncCases(builder, getElecUnitContextThunk, "elecUnitContext");
    addAsyncCases(builder, getElecPendingScheduleThunk, "elecPendingSchedule");
    // Water Rate Configs
    addAsyncCases(builder, getWaterRateConfigsThunk, "waterRateConfigs");
    addAsyncCases(builder, getWaterRateConfigByIdThunk, "waterRateConfigDetail");
    addAsyncCases(builder, createWaterRateConfigThunk, "createWaterRateConfig");
    addAsyncCases(builder, updateWaterRateConfigThunk, "updateWaterRateConfig");
    // Water Records
    addAsyncCases(builder, getWaterPendingScheduleThunk, "waterPendingSchedule");
    addAsyncCases(builder, getWaterUnitContextThunk, "waterUnitContext");
    addAsyncCases(builder, getWaterRecordsThunk, "waterRecords");
    addAsyncCases(builder, getWaterRecordByIdThunk, "waterRecordDetail");
    addAsyncCases(builder, createWaterRecordThunk, "createWaterRecord");
    addAsyncCases(builder, createWaterRecordsBatchThunk, "createWaterRecordsBatch");
    addAsyncCases(builder, updateWaterRecordThunk, "updateWaterRecord");
    addAsyncCases(builder, deleteWaterRecordThunk, "deleteWaterRecord");
    addAsyncCases(builder, updateWaterRecordStatusThunk, "updateWaterRecordStatus");
    addAsyncCases(builder, calculateWaterPreviewThunk, "waterPreview");
  },
});

export const utilityRecordAction = {
  ...utilityRecordSlice.actions,
  getElecRateConfigsThunk,
  getElecRateConfigByIdThunk,
  createElecRateConfigThunk,
  updateElecRateConfigThunk,
  getElecRecordsThunk,
  getElecRecordByIdThunk,
  createElecRecordThunk,
  createElecRecordsBatchThunk,
  updateElecRecordThunk,
  deleteElecRecordThunk,
  updateElecRecordStatusThunk,
  getElecRecordSummaryTextThunk,
  calculateElecPreviewThunk,
  getElecUnitContextThunk,
  getElecPendingScheduleThunk,
  getWaterRateConfigsThunk,
  getWaterRateConfigByIdThunk,
  createWaterRateConfigThunk,
  updateWaterRateConfigThunk,
  getWaterPendingScheduleThunk,
  getWaterRecordsThunk,
  getWaterRecordByIdThunk,
  createWaterRecordThunk,
  createWaterRecordsBatchThunk,
  updateWaterRecordThunk,
  deleteWaterRecordThunk,
  updateWaterRecordStatusThunk,
  calculateWaterPreviewThunk,
  getWaterUnitContextThunk,
};

export function selectUtilityRecord(state) {
  return state?.utilityRecord;
}

export default utilityRecordSlice.reducer;
